import storage from '../../../../hooks/useStorage/useStorage';
import { saleItemIsKit, saleItemIsProductColor } from '../../../catalogs/utils/saleItemType.utils';
import { Role } from '../../../orders/actions/types/actions.types';
import { offlineCatalogsRepository } from '../../repositories/offlineCatalogs.repository';
import { offlineCustomersRepository } from '../../repositories/offlineCustomers.repository';
import { offlineOrdersRepository } from '../../repositories/offlineOrders.repository';
import { offlineUsersRepository } from '../../repositories/offlineUsers.repository';
import {
  ShouldValidateCustomerCatalogsParams,
  ValidateCustomerOnOrderParams,
  ValidateForKitParams,
  ValidateForProductColorParams,
} from '../types/validateCatalogSectionItemForCustomerCatalogsUtils.types';

export class ValidateCatalogSectionItemForCustomerCatalogsUtils {
  static async validateForProductColor({
    customerId,
    productColorId,
    restrictProductsUsingCustomerCatalogs,
  }: ValidateForProductColorParams): Promise<boolean> {
    const teamId = storage.getTeamId();
    const userRole = offlineUsersRepository.getUserTeam(teamId)?.role;
    const shouldValidateCustomerCatalogs = this.shouldValidateCustomerCatalogs({
      restrictProductsUsingCustomerCatalogs,
      userRole,
      customerId,
    });

    if (!shouldValidateCustomerCatalogs) {
      return true;
    }

    const customerCatalogs = await this.getCustomerCatalogs(customerId!);
    return customerCatalogs.some((catalog) =>
      catalog.sections.some((section) =>
        section.items.some(
          (item) => saleItemIsProductColor(item) && item.productColor.id === productColorId,
        ),
      ),
    );
  }

  private static getCustomerCatalogs = async (customerId: string) => {
    const customer = await offlineCustomersRepository.getByIdOrFail(customerId);
    const customerCatalogs = await offlineCatalogsRepository.findByCustomerOrClassification(
      customerId,
      customer.classificationIds,
    );

    return customerCatalogs;
  };

  static async validateForKit({
    customerId,
    kitId,
    restrictProductsUsingCustomerCatalogs,
  }: ValidateForKitParams): Promise<boolean> {
    const teamId = storage.getTeamId();
    const userRole = offlineUsersRepository.getUserTeam(teamId)?.role;
    const shouldValidateCustomerCatalogs = this.shouldValidateCustomerCatalogs({
      restrictProductsUsingCustomerCatalogs,
      userRole,
      customerId,
    });

    if (!shouldValidateCustomerCatalogs) {
      return true;
    }

    const customerCatalogs = await this.getCustomerCatalogs(customerId!);

    return customerCatalogs.some((catalog) =>
      catalog.sections.some((section) =>
        section.items.some((item) => saleItemIsKit(item) && item.kit.id === kitId),
      ),
    );
  }

  static async validateCustomerOnOrder({
    customerId,
    orderId,
    restrictProductsUsingCustomerCatalogs,
  }: ValidateCustomerOnOrderParams): Promise<boolean> {
    const order = await offlineOrdersRepository.getByIdOrThrow(orderId);
    const teamId = storage.getTeamId();
    const userRole = offlineUsersRepository.getUserTeam(teamId)?.role;

    const shouldValidateCustomerCatalogs = this.shouldValidateCustomerCatalogs({
      restrictProductsUsingCustomerCatalogs,
      userRole,
      customerId,
    });
    if (!shouldValidateCustomerCatalogs || !order.products?.length) {
      return true;
    }

    const customerCatalogs = await this.getCustomerCatalogs(customerId!);

    const customerKitIds = new Set<string>();
    const customerProductColorIds = new Set<string>();

    customerCatalogs.forEach((catalog) => {
      catalog.sections.forEach((section) => {
        section.items.forEach((item) => {
          if (saleItemIsKit(item)) {
            customerKitIds.add(item.kit.id);
          } else if (saleItemIsProductColor(item)) {
            customerProductColorIds.add(item.productColor.id);
          }
        });
      });
    });

    return order.products.every((orderProduct) => {
      if (orderProduct.kit?.id) {
        return customerKitIds.has(orderProduct.kit.id);
      }
      return customerProductColorIds.has(orderProduct.productColor.id);
    });
  }

  private static shouldValidateCustomerCatalogs({
    userRole,
    customerId,
    restrictProductsUsingCustomerCatalogs,
  }: ShouldValidateCustomerCatalogsParams) {
    return restrictProductsUsingCustomerCatalogs && userRole === Role.VENDEDOR && !!customerId;
  }
}
